import type { Question, Template } from "@/features/questionnaire/types";

export type ChangedField = "title" | "options" | "type";
export type TemplateDiff = {
  added: Question[];
  removed: string[];
  changed: { id: string; fields: ChangedField[] }[];
};

function questionsById(t: Template | null): Map<string, Question> {
  const map = new Map<string, Question>();
  for (const s of t?.sections ?? []) for (const q of s.questions) map.set(q.id, q);
  return map;
}

const optionSig = (q: Question) => JSON.stringify((q.options ?? []).map((o) => [o.value, o.label]));

/** Client-side preview of what changes between two bodies. The server diff is authoritative. */
export function diffTemplates(prev: Template | null, next: Template): TemplateDiff {
  const before = questionsById(prev);
  const after = questionsById(next);
  const added: Question[] = [];
  const changed: TemplateDiff["changed"] = [];
  for (const [id, q] of after) {
    const old = before.get(id);
    if (!old) {
      added.push(q);
      continue;
    }
    const fields: ChangedField[] = [];
    if (old.title !== q.title) fields.push("title");
    if (optionSig(old) !== optionSig(q)) fields.push("options");
    if (old.type !== q.type) fields.push("type");
    if (fields.length > 0) changed.push({ id, fields });
  }
  const removed = [...before.keys()].filter((id) => !after.has(id));
  return { added, removed, changed };
}
